import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';

const EXTENSION_URL =
  process.env.VITE_EXTENSION_URL ||
  'https://chromewebstore.google.com/detail/unbeatable-extension/effibccnihcalkgiimempmhiabigndfl';

const PHASE_MS = 3200;

const pulseRing = keyframes`
  0% { transform: scale(0.95); opacity: 0.6; }
  70% { transform: scale(1.08); opacity: 0; }
  100% { transform: scale(1.08); opacity: 0; }
`;

const flowDot = keyframes`
  0%, 100% { transform: translateX(0); opacity: 0.35; }
  50% { transform: translateX(6px); opacity: 1; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PICKER_FIELDS = [
  { id: 'price', label: 'List price', value: '$485,000' },
  { id: 'address', label: 'Address', value: '142 Oak Lane, Austin TX' },
  { id: 'beds', label: 'Bedrooms', value: '3 bd' },
  { id: 'baths', label: 'Bathrooms', value: '2 ba' },
  { id: 'sqft', label: 'Square feet', value: '1,840 sqft' },
  { id: 'tax', label: 'Property tax', value: '$6,200/yr' },
];

const PHASES = [
  {
    id: 0,
    title: 'Browse any listing',
    caption: 'Open a home on Zillow, Redfin, or any site.',
  },
  {
    id: 1,
    title: 'Pick what to import',
    caption: 'Use the extension picker to choose fields to send.',
  },
  {
    id: 2,
    title: 'Save to Unbeatable',
    caption: 'Listing details land in your portfolio instantly.',
  },
  {
    id: 3,
    title: 'Get loan estimates',
    caption: 'See payments and programs tailored to the property.',
  },
  {
    id: 4,
    title: 'Submit an offer',
    caption: 'Move from estimate to offer without leaving the platform.',
  },
] as const;

function PhaseDots({
  active,
  onSelect,
}: {
  active: number;
  onSelect: (i: number) => void;
}) {
  return (
    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
      {PHASES.map((p) => (
        <Box
          key={p.id}
          component="button"
          type="button"
          aria-label={p.title}
          onClick={() => onSelect(p.id)}
          sx={{
            width: active === p.id ? 22 : 8,
            height: 8,
            borderRadius: 999,
            border: 'none',
            p: 0,
            cursor: 'pointer',
            bgcolor: active === p.id ? 'primary.main' : 'action.selected',
            transition: 'width 0.25s ease, background-color 0.25s ease',
          }}
        />
      ))}
    </Stack>
  );
}

export const PurchaseExtensionOnboardingDemo = () => {
  const theme = useTheme();
  const [phase, setPhase] = useState(0);
  const [checkedCount, setCheckedCount] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setPhase((p) => (p + 1) % PHASES.length);
    }, PHASE_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (phase !== 1) {
      setCheckedCount(phase > 1 ? PICKER_FIELDS.length : 0);
      return;
    }
    setCheckedCount(0);
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setCheckedCount(Math.min(i, PICKER_FIELDS.length));
      if (i >= PICKER_FIELDS.length) window.clearInterval(id);
    }, 420);
    return () => window.clearInterval(id);
  }, [phase]);

  const frameSx = {
    position: 'relative' as const,
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    bgcolor: alpha(theme.palette.common.white, 0.92),
    overflow: 'hidden',
    minHeight: 220,
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        How the Chrome extension works
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {PHASES[phase].caption}
      </Typography>

      <Box sx={frameSx}>
        {/* Browser chrome */}
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            bgcolor: alpha(theme.palette.grey[500], 0.12),
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.grey[600], 0.35),
                }}
              />
            ))}
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              mx: 1,
              py: 0.4,
              px: 1,
              borderRadius: 1,
              bgcolor: 'background.paper',
              fontSize: 11,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            listing-site.com/property/142-oak-lane
          </Box>
          <Box sx={{ position: 'relative' }}>
            {phase <= 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: -6,
                  borderRadius: 1,
                  border: `2px solid ${theme.palette.primary.main}`,
                  animation: `${pulseRing} 1.8s ease-out infinite`,
                }}
              />
            )}
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              UL
            </Box>
          </Box>
        </Box>

        {/* Tall enough for the picker (the tallest phase): the frame clips
            overflow, and a fixed height keeps the dialog from jumping. */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            position: 'relative',
            minHeight: { xs: 300, sm: 290 },
          }}
        >
          {/* Listing mock */}
          {(phase === 0 || phase === 1) && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ animation: `${fadeUp} 0.45s ease both` }}
            >
              <Box
                sx={{
                  width: { xs: '100%', sm: 120 },
                  height: 88,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  142 Oak Lane
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Austin, TX · 3 bd · 2 ba · 1,840 sqft
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, mt: 0.5, color: 'primary.main' }}
                >
                  $485,000
                </Typography>
              </Box>
            </Stack>
          )}

          {/* Extension picker */}
          {phase === 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                left: { xs: 12, sm: 'auto' },
                width: { sm: 240 },
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: `0 12px 32px ${alpha(
                  theme.palette.common.black,
                  0.18
                )}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                animation: `${fadeUp} 0.35s ease both`,
                zIndex: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, display: 'block', mb: 1 }}
              >
                Send to Unbeatable
              </Typography>
              <Stack spacing={0.25}>
                {PICKER_FIELDS.map((field, idx) => (
                  <Stack
                    key={field.id}
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      py: 0.25,
                      opacity: idx < checkedCount ? 1 : 0.45,
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={idx < checkedCount}
                      tabIndex={-1}
                      disableRipple
                      sx={{ p: 0.25, pointerEvents: 'none' }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, lineHeight: 1.2 }}
                      >
                        {field.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {field.value}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
              <Button
                size="small"
                variant="contained"
                fullWidth
                sx={{ mt: 1, fontWeight: 700 }}
                disabled={checkedCount < PICKER_FIELDS.length}
              >
                Add to Unbeatable
              </Button>
            </Box>
          )}

          {/* Import + platform */}
          {phase === 2 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ py: 2, animation: `${fadeUp} 0.45s ease both` }}
            >
              <Chip label="Listing imported" color="primary" size="small" />
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={0.5}
                alignItems="center"
              >
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      animation: `${flowDot} 1.2s ease-in-out infinite`,
                      animationDelay: `${i * 0.15}s`,
                    }}
                  />
                ))}
              </Stack>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  minWidth: 160,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  My Properties
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  142 Oak Lane
                </Typography>
                <Typography variant="caption" color="primary.main">
                  Ready for estimates
                </Typography>
              </Box>
            </Stack>
          )}

          {/* Loan estimate */}
          {phase === 3 && (
            <Box sx={{ animation: `${fadeUp} 0.45s ease both` }}>
              <Typography variant="caption" color="text.secondary">
                Loan estimate · Conventional
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 1 }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Rate
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    6.42%
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Monthly PITI
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    $3,128
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Down
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    10%
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Offer */}
          {phase === 4 && (
            <Stack
              alignItems="center"
              spacing={1.5}
              sx={{ py: 2, animation: `${fadeUp} 0.45s ease both` }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i
                  className="fa-light fa-file-signature"
                  style={{ fontSize: 24 }}
                />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Offer ready to submit
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Use deal-room tools to negotiate and send your purchase
                contract.
              </Typography>
              <Button variant="contained" size="small" sx={{ fontWeight: 700 }}>
                Submit offer
              </Button>
            </Stack>
          )}
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', textAlign: 'center', mt: 1, fontWeight: 600 }}
      >
        {PHASES[phase].title}
      </Typography>
      <PhaseDots active={phase} onSelect={setPhase} />

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Button
          component="a"
          href={EXTENSION_URL}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          variant="outlined"
        >
          Get the Chrome extension
        </Button>
      </Box>
    </Box>
  );
};
